import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, LockIcon, ZapIcon, DownloadIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
export function HeroSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const benefits = ['Reduce la hinchazón abdominal', 'Mejora tu digestión naturalmente', 'Recupera tu energía diaria', 'Hábitos simples y fáciles de seguir', 'Diseñado para mujeres +40'];
  return <>
      {/* Top Banner */}
      <div className="bg-terracotta text-white text-center py-2.5 px-4 font-semibold text-sm tracking-wide relative z-50" data-id="element-69">
        🔥 Oferta limitada — ¡Aprovecha el precio especial hoy!
      </div>

      <section className="relative overflow-hidden pt-16 pb-24 lg:pt-28 lg:pb-32 min-h-[90vh] flex items-center" data-id="element-70">
        {/* Background: blurred food image */}
        <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-71">
          <img src="https://cdn.magicpatterns.com/uploads/wN8wkhqQmMeNhovQuoHbtX/Portada_libro_2.png" alt="Wellness background" className="w-full h-full object-cover blur-md scale-110" data-id="element-72" />
          {/* Warm overlay for readability */}
          <div className="absolute inset-0 bg-[#3A4A4A]/45" data-id="element-73"></div>
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#3A4A4A]/25 via-transparent to-[#3A4A4A]/15" data-id="element-74"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10" data-id="element-75">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12 items-center" data-id="element-76">
            {/* Left Column: Copy & CTA */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-xl mx-auto lg:mx-0" data-id="element-77">
              <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-[1.1] mb-6 drop-shadow-lg" data-id="element-78">
                DESINFLAMA TU CUERPO <br data-id="element-79" />
                <span className="text-terracotta drop-shadow-md" data-id="element-80">
                  EN SOLO 14 DÍAS
                </span>
              </motion.h1>

              <motion.p variants={itemVariants} className="text-lg md:text-xl text-white/85 mb-8 leading-relaxed" data-id="element-81">
                Un plan simple con hábitos fáciles que pueden ayudarte a reducir
                la inflamación, mejorar tu digestión y recuperar tu energía.
              </motion.p>

              <motion.ul variants={itemVariants} className="space-y-3.5 mb-10" data-id="element-82">
                {benefits.map((benefit, i) => <li key={i} className="flex items-start gap-3" data-id="element-83">
                    <div className="mt-0.5 bg-sage p-1 rounded-full text-white flex-shrink-0" data-id="element-84">
                      <CheckIcon size={16} strokeWidth={3} data-id="element-85" />
                    </div>
                    <span className="text-white font-medium text-lg drop-shadow-sm" data-id="element-86">
                      {benefit}
                    </span>
                  </li>)}
              </motion.ul>

              <motion.div variants={itemVariants} className="bg-white p-6 sm:p-8 rounded-3xl shadow-soft-xl border border-beige-light mb-8 flex flex-col sm:flex-row items-center sm:items-end gap-6 relative overflow-hidden" data-id="element-87">
                <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-88"></div>

                <div className="text-center sm:text-left relative z-10" data-id="element-89">
                  <span className="block text-text-medium line-through text-sm font-semibold mb-1" data-id="element-90">
                    Antes $47
                  </span>
                  <div className="flex items-start justify-center sm:justify-start" data-id="element-91">
                    <span className="text-5xl md:text-6xl font-heading font-bold text-terracotta leading-none tracking-tight" data-id="element-92">
                      Solo $6.39
                    </span>
                  </div>
                </div>

                <div className="flex-1 w-full sm:w-auto relative z-10" data-id="element-93">
                  <CTAButton text="QUIERO EMPEZAR HOY" variant="primary" href="#oferta" className="w-full text-lg py-4 shadow-xl shadow-terracotta/25" data-id="element-94" />
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex flex-wrap justify-center sm:justify-start items-center gap-5 md:gap-8 text-sm text-white/80 font-medium" data-id="element-95">
                <span className="flex items-center gap-2" data-id="element-96">
                  <LockIcon size={18} className="text-sage" data-id="element-97" /> Pago seguro
                </span>
                <span className="flex items-center gap-2" data-id="element-98">
                  <ZapIcon size={18} className="text-gold-soft" data-id="element-99" /> Acceso
                  inmediato
                </span>
                <span className="flex items-center gap-2" data-id="element-100">
                  <DownloadIcon size={18} className="text-sage" data-id="element-101" /> Descarga
                  digital
                </span>
              </motion.div>
            </motion.div>

            {/* Right Column: Product Mockup */}
            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4,
            ease: 'easeOut'
          }} className="relative w-full max-w-[500px] mx-auto lg:ml-auto mt-10 lg:mt-0" data-id="element-102">
              {/* Continuous floating animation wrapper */}
              <motion.div animate={{
              y: [0, -15, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative" data-id="element-103">
                {/* Background Glow behind devices */}
                <div className="absolute inset-0 bg-sage/40 blur-[60px] rounded-full transform scale-110" data-id="element-104"></div>

                {/* Laptop Mockup */}
                <div className="relative z-10 transform -rotate-2 hover:rotate-0 transition-transform duration-700 ease-out" data-id="element-105">
                  {/* Screen Bezel */}
                  <div className="bg-[#2C302B] p-2.5 md:p-3.5 rounded-t-2xl rounded-b-sm shadow-2xl border-b-2 border-gray-800 relative" data-id="element-106">
                    {/* Webcam dot */}
                    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-gray-600 rounded-full" data-id="element-107"></div>

                    {/* Screen Content */}
                    <div className="aspect-[16/10] rounded-lg relative overflow-hidden shadow-inner border border-white/10 bg-[#F0EBE3]" data-id="element-108">
                      <img src="https://cdn.magicpatterns.com/uploads/wN8wkhqQmMeNhovQuoHbtX/Portada_libro_2.png" alt="14 Días Para Desinflamar Tu Cuerpo - Guía Práctica" className="w-full h-full object-contain object-center" data-id="element-109" />
                      {/* Screen Gloss/Reflection */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/8 to-white/0 transform -skew-x-12 translate-x-1/3 pointer-events-none" data-id="element-110"></div>
                    </div>
                  </div>

                  {/* Keyboard Base */}
                  <div className="bg-[#E9ECE8] h-3 md:h-4 w-[114%] -ml-[7%] rounded-b-2xl shadow-xl border-t border-white/60 flex justify-center relative overflow-hidden" data-id="element-111">
                    {/* Trackpad indent */}
                    <div className="w-1/4 h-1 md:h-1.5 bg-gray-300/80 rounded-b-md" data-id="element-112"></div>
                    {/* Base edge highlight */}
                    <div className="absolute bottom-0 left-0 w-full h-[1px] bg-black/10" data-id="element-113"></div>
                  </div>
                </div>

                {/* Smartphone Mockup */}
                <div className="absolute -bottom-10 -right-4 md:-right-12 z-20 transform rotate-6 hover:rotate-2 transition-transform duration-700 ease-out w-[130px] md:w-[170px]" data-id="element-114">
                  {/* Phone Bezel */}
                  <div className="bg-[#2C302B] p-1.5 md:p-2 rounded-[2rem] shadow-2xl border border-gray-700 relative" data-id="element-115">
                    {/* Volume buttons */}
                    <div className="absolute top-16 -left-[2px] w-[2px] h-8 bg-gray-600 rounded-l-sm" data-id="element-116"></div>
                    <div className="absolute top-28 -left-[2px] w-[2px] h-8 bg-gray-600 rounded-l-sm" data-id="element-117"></div>
                    {/* Power button */}
                    <div className="absolute top-20 -right-[2px] w-[2px] h-10 bg-gray-600 rounded-r-sm" data-id="element-118"></div>

                    {/* Phone Screen */}
                    <div className="aspect-[9/16] rounded-[1.5rem] relative overflow-hidden shadow-inner border border-white/10 bg-[#F0EBE3]" data-id="element-119">
                      {/* Notch */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1/3 h-4 bg-[#2C302B] rounded-b-xl z-20" data-id="element-120"></div>
                      <img src="https://cdn.magicpatterns.com/uploads/ddfhVqrmmHaf8FXnjDwX92/Portada_libro.png" alt="14 Días Para Desinflamar Tu Cuerpo - Guía Práctica" className="w-full h-full object-contain object-center" data-id="element-121" />
                      {/* Phone Screen Gloss */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/8 to-white/0 transform -skew-x-12 translate-x-1/4 pointer-events-none" data-id="element-122"></div>
                    </div>
                  </div>

                  {/* Phone shadow on laptop */}
                  <div className="absolute -inset-4 bg-black/20 blur-xl rounded-full -z-10 transform translate-y-4 translate-x-4" data-id="element-123"></div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}